<template>
  <div class="rows mobile-cols">
    <div class="half">
      <DataGrid
          :actions-header="$t('settings.on-board.users')"
          :data-model="dataGridModel"
          :data="filteredUsers"
          :selected-id="selectedUserId"
          @on-select="dataGridSelectUserId"
          allow-add
          allow-edit
      />
    </div>
    <div class="half">
      <div class="data-grid-form cols">
        <div class="rows phone-cols">
          <div class="half">
            <label for="userName">
              {{ $t('settings.on-board.users-name') }}
            </label>
            <input
                id="userName"
                class="stretch"
                v-model="name"
                :readonly="selectedUserId !== null"
            />
          </div>
          <div class="half">
            <label for="userEmail">
              {{ $t('settings.on-board.users-email') }}
            </label>
            <input
                id="userEmail"
                class="stretch"
                v-model="email"
                :readonly="selectedUserId !== null"
            />
          </div>
        </div>
        <div class="cols">
          <div v-if="selectedUserId === null">
            <label for="userPassword">
              {{ $t('settings.on-board.users-password') }}
            </label>
            <input
                id="userPassword"
                type="password"
                class="stretch"
                v-model="password"
            />
          </div>
          <div>
            <label for="selectedCompany">
              {{ $t('generic.company') }}
            </label>
            <multiselect id="selectedCompany"
                         v-model="selectedCompany"
                         :options="companiesOptions"
                         :close-on-select="true"
                         :clear-on-select="false"
                         :preserve-search="true"
                         :show-labels="false"
                         :placeholder="$t('settings.generic.select-items')"
                         :show-no-results="false"
                         :show-no-options="false"
                         :allow-empty="false"
                         :preselect-first="true"
                         label="name" track-by="id"
                         class="tracking-multiselect stretch">
              <template #option="props">
                <div class="tracking-multiselect-with-checkbox">
                    <span class="width-20">
                      <i v-if="selectedCompany?.id === props.option.id" class="bi bi-check-lg width-15"></i>
                    </span>
                  <span class="option__title">{{ props.option.name }}</span>
                </div>
              </template>
            </multiselect>
          </div>
          <div>
            <label for="companyName">
              {{ $t('settings.on-board.users.form.company-name') }}
            </label>
            <input
                id="companyName"
                class="stretch"
                :class="{'is-invalid': showStateByField('name') === false}"
                v-model="companyName"
            />
          </div>
          <div>
            <label for="companyAddress">
              {{ $t('settings.on-board.users.form.company-address') }}
            </label>
            <input
                id="companyAddress"
                class="stretch"
                :class="{'is-invalid': showStateByField('address') === false}"
                v-model="companyAddress"
            />
          </div>
        </div>
        <div class="rows mobile-cols">
          <div class="half">
            <label for="companyPic">
              {{ $t('settings.on-board.users.form.company-pic') }}
            </label>
            <input
                id="companyPic"
                class="stretch"
                :class="{'is-invalid': showStateByField('pic') === false}"
                v-model="companyPic"
            />
          </div>
          <div class="half">
            <label for="companyManagerName">
              {{ $t('settings.on-board.users.form.company-manager-name') }}
            </label>
            <input
                id="companyManagerName"
                class="stretch"
                :class="{'is-invalid': showStateByField('managerName') === false}"
                v-model="companyManagerName"
            />
          </div>
        </div>
        <div class="rows mobile-cols">
          <div class="half">
            <label for="companyWorkHoursStart">
              {{ $t('settings.on-board.users.form.company-work-hours-start') }}
            </label>
            <Datepicker
                id="companyWorkHoursStart"
                v-model="companyWorkHoursStartData"
                time-picker
                auto-apply
                :state="showStateByField('workHoursStart')"
            />
          </div>
          <div class="half">
            <label for="companyWorkHoursEnd">
              {{ $t('settings.on-board.users.form.company-work-hours-end') }}
            </label>
            <Datepicker
                id="companyWorkHoursEnd"
                v-model="companyWorkHoursEndData"
                time-picker
                auto-apply
                :state="showStateByField('workHoursEnd')"
            />
          </div>
        </div>
        <fieldset>
          <legend>{{ $t('settings.on-board.users.form.legal-entity-data') }}</legend>
          <div class="rows mobile-cols">
            <div class="half">
              <label for="legalEntityIdentityNumber">
                {{ $t('settings.on-board.users.form.legal-entity-identity-number') }}
              </label>
              <input
                  id="legalEntityIdentityNumber"
                  class="stretch"
                  :class="{'is-invalid': showStateByField('legalEntityIdentityNumber') === false}"
                  v-model="legalEntityIdentityNumber"
              />
            </div>
            <div class="half">
              <label for="legalEntityDocumentNumber">
                {{ $t('settings.on-board.users.form.legal-entity-document-number') }}
              </label>
              <input
                  id="legalEntityDocumentNumber"
                  class="stretch"
                  :class="{'is-invalid': showStateByField('legalEntityDocumentNumber') === false}"
                  v-model="legalEntityDocumentNumber"
              />
            </div>
          </div>
        </fieldset>
        <fieldset>
          <legend>{{ $t('settings.on-board.users.form.task-link-notification-methods') }}</legend>
          <div class="rows mobile-cols">
            <div v-for="method in taskLinkNotificationMethodOptions" :key="method.value">
              <input type="checkbox"
                     :id="`taskLinkNotificationMethod${method.value}`"
                     :value="method.value"
                     class="big-check-box"
                     v-model="taskLinkNotificationMethods"
              />
              <label :for="`taskLinkNotificationMethod${method.value}`">
                {{ $t(method.translationKey) }}
              </label>
            </div>
          </div>
        </fieldset>
        <div class="cols">
          <label>
            {{ $t('settings.on-board.users.check-violations') }}:
          </label>
          <div class="rows mobile-cols">
            <div>
              <input type="radio"
                     id="trafficViolationsRequestTypeAsPhysicalPerson"
                     name="trafficViolationsRequestType"
                     :value="TrafficViolationsRequestType.AsPhysicalPerson"
                     class="big-check-box"
                     v-model="trafficViolationsRequestType"
              />
              <label for="trafficViolationsRequestTypeAsPhysicalPerson">
                {{ $t('settings.on-board.users.physical-person') }}
              </label>
            </div>
            <div>
              <input type="radio"
                     id="trafficViolationsRequestTypeAsCompany"
                     name="trafficViolationsRequestType"
                     :value="TrafficViolationsRequestType.AsCompany"
                     class="big-check-box"
                     v-model="trafficViolationsRequestType"
              />
              <label for="trafficViolationsRequestTypeAsCompany">
                {{ $t('settings.on-board.users.company') }}
              </label>
            </div>
            <div>
              <input type="radio"
                     id="trafficViolationsRequestTypeNoRequest"
                     name="trafficViolationsRequestType"
                     :value="TrafficViolationsRequestType.NoRequest"
                     class="big-check-box"
                     v-model="trafficViolationsRequestType"
              />
              <label for="trafficViolationsRequestTypeNoRequest">
                {{ $t('settings.on-board.users.no-validation') }}
              </label>
            </div>
          </div>
        </div>
        <div class="rows mobile-cols">
          <div>
            <input type="checkbox"
                   id="showDeviceNotifications"
                   class="big-check-box"
                   v-model="showDeviceNotifications"
            />
            <label for="showDeviceNotifications">
              {{ $t('settings.on-board.users.show-device-notifications') }}
            </label>
          </div>
        </div>
        <div class="rows mobile-cols">
          <div class="full">
            <input
                type="checkbox"
                id="allowFullVehicleEdit"
                class="big-check-box"
                v-model="allowFullVehicleEdit"
            />
            <label for="allowFullVehicleEdit">
              {{ $t('settings.on-board.users.form.allow-full-vehicle-edit') }}
            </label>
          </div>
        </div>

        <div>
          <button type="submit" class="button-save" @click="save">
            {{ $t('generic.save') }}
          </button>
        </div>

        <h3>{{ $t('settings.on-board.tasks') }}</h3>
        <TaskList :tasks="tasks"/>
      </div>
    </div>
  </div>
</template>

<script>
import DataGrid from '@/components/pages/partials/DataGrid';
import {mapState} from 'vuex';
import {actions} from '@/assets/js/vue/vuex/actions';
import notification from '@/assets/js/notification';
import {dataAggregator, dataManipulator, entityDataManager} from '@/assets/js/data-manipulation';
import TaskList from '@/components/pages/partials/TaskList';
import Datepicker from '@vuepic/vue-datepicker';
import {TrafficViolationsRequestType} from "@/assets/js/enums/TrafficViolationsRequestType.ts";
import {TaskLinkNotificationMethod} from "@/assets/js/enums/TaskLinkNotificationMethod.ts";
import Multiselect from "vue-multiselect";

export default {
  name: 'Users',
  components: {Multiselect, TaskList, DataGrid, Datepicker},
  props: {
    searchToken: String,
    selectedUserId: [Number, null],
  },
  data() {
    return {
      fieldStates: {},

      name: null,
      email: null,
      password: null,
      selectedCompany: null,
      companyName: null,
      companyAddress: null,
      companyPic: null,
      companyManagerName: null,
      companyWorkHoursStartData: {
        hours: 9,
        minutes: 0,
        seconds: 0,
      },
      companyWorkHoursEndData: {
        hours: 18,
        minutes: 0,
        seconds: 0,
      },
      legalEntityIdentityNumber: null,
      legalEntityDocumentNumber: null,
      allowFullVehicleEdit: null,
      showDeviceNotifications: true,
      trafficViolationsRequestType: TrafficViolationsRequestType.NoRequest,
      taskLinkNotificationMethods: [TaskLinkNotificationMethod.Sms],

      tasks: {
        taskCreateUser: {
          text: this.$t('settings.on-board.users.tasks.create-user'),
          status: 'pending',
        },
        taskCreateGroup: {
          text: this.$t('settings.on-board.users.tasks.create-group'),
          status: 'pending',
        },
        taskCreateUserLink: {
          text: this.$t('settings.on-board.users.tasks.create-user-link'),
          status: 'pending',
        },
        taskCreateGroupLink: {
          text: this.$t('settings.on-board.users.tasks.create-group-link'),
          status: 'pending',
        },
        taskCreateCompany: {
          text: this.$t('settings.on-board.users.tasks.create-company'),
          status: 'pending',
        },
      },
    };
  },
  computed: {
    ...mapState(['users', 'companies', 'lastErrorDetails']),
    companiesOptions() {
      return [
          ...[this.newCompany],
          ...this.companies
      ]
    },
    newCompany() {
      return {id: null, name: this.$t('settings.on-board.users.new-company') };
    },
    dataGridModel() {
      return {
        headers: {
          [this.$t('settings.on-board.users-email')]: 'email',
        },
      };
    },
    companyWorkHoursStart() {
      return dataManipulator.timeObjToString(this.companyWorkHoursStartData);
    },
    companyWorkHoursEnd() {
      return dataManipulator.timeObjToString(this.companyWorkHoursEndData);
    },
    filteredUsers() {
      return dataAggregator.filterListByToken(this.users, ['name', 'email'], this.searchToken);
    },
    hasErrors() {
      return Object.values(this.fieldStates).length > 0;
    },
    TrafficViolationsRequestType() {
      return TrafficViolationsRequestType
    },
    taskLinkNotificationMethodOptions() {
      return TaskLinkNotificationMethod.all();
    },
  },
  watch: {
    selectedUserId(id) {
      this.fillFormById(id);
    },
    selectedCompany() {
      this.fillCompanyFields(this.selectedCompany);
    }
  },
  methods: {
    dataGridSelectUserId(id) {
      this.selectUserId(id);
      this.resetTasks();
    },
    selectUserId(id) {
      this.fillFormById(id);
      this.$emit('on-select-user', id);
    },
    showDirtyFields() {
      this.fieldStates = {};

      if (this.lastErrorDetails === null) {
        return;
      }

      this.lastErrorDetails.forEach(error => {
        if (error.field !== undefined) {
          this.fieldStates[error.field] = false;
        }
      });
    },
    showStateByField(field) {
      return entityDataManager.getStateByField(this.fieldStates, field);
    },
    resetTasks() {
      for (let task in this.tasks) {
        this.tasks[task].status = 'pending';
      }
    },
    succeedTask(task) {
      this.tasks[task].status = 'success';
    },
    failTask(task) {
      this.tasks[task].status = 'fail';
    },
    fillFormById(id) {
      this.password = null;

      if (id === null) {
        this.name = null;
        this.email = null;

        this.clearCompanyFields();

        return;
      }

      const user = entityDataManager.getById(this.users, id);

      if (user) {
        this.name = user.name;
        this.email = user.email;
      }

      this.selectedCompany = entityDataManager.getCompanyByUserId(this.companies, id);
      this.fillCompanyFields(this.selectedCompany);
    },
    fillCompanyFields(company) {
      if (!company?.id) {
        this.clearCompanyFields();

        return;
      }

      this.companyName = company.name;
      this.companyAddress = company.address;
      this.companyPic = company.pic;
      this.companyManagerName = company.managerName;
      this.companyWorkHoursStartData = dataManipulator.timeStringToObj(company.workHoursStart);
      this.companyWorkHoursEndData = dataManipulator.timeStringToObj(company.workHoursEnd);
      this.legalEntityIdentityNumber = company.legalEntityIdentityNumber;
      this.legalEntityDocumentNumber = company.legalEntityDocumentNumber;
      this.allowFullVehicleEdit = company.allowFullVehicleEdit;
      this.showDeviceNotifications = company.showDeviceNotifications;
      this.trafficViolationsRequestType = company.trafficViolationsRequestType;
      this.taskLinkNotificationMethods = company.taskLinkNotificationMethods ?? [TaskLinkNotificationMethod.Sms];
    },
    clearCompanyFields() {
      this.selectedCompany = this.newCompany;
      this.companyName = null;
      this.companyAddress = null;
      this.companyPic = null;
      this.companyManagerName = null;
      this.companyWorkHoursStartData = {hours: 9, minutes: 0, seconds: 0};
      this.companyWorkHoursEndData = {hours: 18, minutes: 0, seconds: 0};
      this.legalEntityIdentityNumber = null;
      this.legalEntityDocumentNumber = null;
      this.allowFullVehicleEdit = null;
      this.showDeviceNotifications = true;
      this.trafficViolationsRequestType = TrafficViolationsRequestType.NoRequest;
      this.taskLinkNotificationMethods = [TaskLinkNotificationMethod.Sms];
    },
    async save() {
      let userId = this.selectedUserId;

      if (userId === null) {
        const userPayload = {
          id: null,
          name: this.name,
          email: this.email,
          password: this.password,
          readonly: true,
          deviceReadonly: true,
        };

        const groupPayload = {
          name: this.name,
        };

        const user = await this.saveUser(userPayload);
        const group = await this.saveGroup(groupPayload);

        if (user === null || group === null) {
          return;
        }

        const userPermissionPayload = {
          userId: process.env.VUE_APP_ADMIN_ID,
          managedUserId: user.id,
        };

        const groupPermissionPayload = {
          userId: user.id,
          groupId: group.id,
        };

        await this.saveUserPermission(userPermissionPayload);
        await this.saveGroupPermission(groupPermissionPayload);

        const attributes = await this.$store.dispatch(actions.GET_ATTRIBUTES);
        const motionTimeAttribute = attributes.find(attribute => attribute.attribute === 'motionTime');

        if (motionTimeAttribute) {
          const groupAttributePermissions = {
            groupId: group.id,
            attributeId: motionTimeAttribute.id,
          };
          await this.saveGroupPermission(groupAttributePermissions);
        }

        const fuelLevelPercentageAttribute = attributes.find(attribute => attribute.attribute === 'fuelLevelPercentage');

        if (fuelLevelPercentageAttribute) {
          const groupAttributePermissions = {
            groupId: group.id,
            attributeId: fuelLevelPercentageAttribute.id,
          };
          await this.saveGroupPermission(groupAttributePermissions);
        }

        userId = user.id;
      }

      const companyPayload = {
        id: this.selectedCompany?.id,
        userId, // todo: Should be removed
        name: this.companyName,
        address: this.companyAddress,
        pic: this.companyPic,
        managerName: this.companyManagerName,
        workHoursStart: this.companyWorkHoursStart,
        workHoursEnd: this.companyWorkHoursEnd,
        legalEntityIdentityNumber: this.legalEntityIdentityNumber,
        legalEntityDocumentNumber: this.legalEntityDocumentNumber,
        allowFullVehicleEdit: this.allowFullVehicleEdit,
        showDeviceNotifications: this.showDeviceNotifications,
        trafficViolationsRequestType: (isNaN(this.trafficViolationsRequestType) ? TrafficViolationsRequestType.NoRequest : parseInt(this.trafficViolationsRequestType)),
        taskLinkNotificationMethods: this.taskLinkNotificationMethods,
        assignedUsers: [{userId}]
      };

      await this.saveCompany(companyPayload);

      await this.$store.dispatch(actions.GET_USERS).catch(e => notification.error(this.$t(e.message)));
      await this.$store.dispatch(actions.GET_COMPANIES).catch(e => notification.error(this.$t(e.message)));

      if (!this.hasErrors) {
        this.selectUserId(userId);
      }
    },
    async saveUser(payload) {
      let resultUser = null;

      await this.$store.dispatch(actions.CREATE_USER, payload).
          then(
              user => {
                resultUser = user;
                this.succeedTask('taskCreateUser');
              },
          ).
          catch(() => this.failTask('taskCreateUser'));

      return resultUser;
    },
    async saveGroup(payload) {
      let resultGroup = null;

      await this.$store.dispatch(actions.CREATE_GROUP, payload).
          then(
              group => {
                resultGroup = group;
                this.succeedTask('taskCreateGroup');
              },
          ).
          catch(() => this.failTask('taskCreateGroup'));

      return resultGroup;
    },
    async saveUserPermission(payload) {
      await this.$store.dispatch(actions.CREATE_PERMISSION, payload).
          then(() => this.succeedTask('taskCreateUserLink')).
          catch(() => this.failTask('taskCreateUserLink'));
    },
    async saveGroupPermission(payload) {
      await this.$store.dispatch(actions.CREATE_PERMISSION, payload).
          then(() => this.succeedTask('taskCreateGroupLink')).
          catch(() => this.failTask('taskCreateGroupLink'));
    },
    async saveCompany(payload) {
      let resultCompany = null;

      const action = payload.id === null ? actions.CREATE_COMPANY : actions.EDIT_COMPANY;

      await this.$store.dispatch(action, payload).
          then(company => {
            resultCompany = company;
            this.succeedTask('taskCreateCompany');
          }).
          catch(() => this.failTask('taskCreateCompany')).
          finally(() => this.showDirtyFields());

      return resultCompany;
    },
  },
};
</script>
